"use client";

import { useEffect, useState, useMemo } from "react";
import { useTranslations } from "next-intl";
import ProductCarousel from "@/components/ProductCarousel";
import { getOtherRegionProducts } from "@/app/actions/products";
import type { Product } from "@/types";

interface OtherRegionProductsProps {
  categoryId?: string;
  currentLocation: string;
  excludeProductIds?: string[];
  locale: string;
  /** 레이아웃 모드: horizontal(가로 스크롤) 또는 vertical(세로 그리드) */
  layout?: "horizontal" | "vertical";
}

export default function OtherRegionProducts({
  categoryId,
  currentLocation,
  excludeProductIds = [],
  locale,
  layout = "horizontal",
}: OtherRegionProductsProps) {
  const t = useTranslations("search");
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // 배열을 문자열로 변환하여 안정적인 dependency로 사용
  const excludeIdsKey = useMemo(
    () => excludeProductIds.join(","),
    [excludeProductIds]
  );

  useEffect(() => {
    async function fetchProducts() {
      setIsLoading(true);
      try {
        const excludeIds = excludeIdsKey ? excludeIdsKey.split(",") : [];
        const data = await getOtherRegionProducts({
          categoryId,
          excludeLocation: currentLocation,
          excludeProductIds: excludeIds,
          limit: 6,
          locale,
        });
        setProducts(data);
      } catch (error) {
        console.error("Failed to fetch other region products:", error);
      } finally {
        setIsLoading(false);
      }
    }

    if (currentLocation) {
      fetchProducts();
    } else {
      setIsLoading(false);
    }
  }, [categoryId, currentLocation, excludeIdsKey, locale]);

  return (
    <ProductCarousel
      products={products}
      isLoading={isLoading}
      title={t("recommendations.otherRegions")}
      description={t("recommendations.otherRegionsDescription")}
      layout={layout}
    />
  );
}
